/* Optional feature components inside core UI. Core never imports a feature statically: each call site
   passes `import.meta.glob('../features/<id>/<File>.tsx')` (a literal, so Vite can split it), and gets a
   lazy component, or null when that feature is not installed. `<OptionalSlot>` renders it with a fallback
   while it loads, and falls back for good if it throws (a feature still in flight never breaks the shell). */
import { Component, lazy, Suspense, type ComponentType, type ErrorInfo, type ReactNode } from 'react';

export type ModuleLoader = () => Promise<unknown>;

/** The loader of the first matching module, or null. */
export function firstLoader(mods: Record<string, ModuleLoader>): ModuleLoader | null {
  const key = Object.keys(mods)[0];
  return key ? mods[key]! : null;
}

const cache = new WeakMap<ModuleLoader, Map<string, ComponentType<never>>>();

/** A lazy component for export `name` of the first module in `mods` (null when there is none). */
export function optionalComponent<P>(mods: Record<string, ModuleLoader>, name = 'default'): ComponentType<P> | null {
  const load = firstLoader(mods);
  if (!load) return null;
  let byName = cache.get(load);
  if (!byName) { byName = new Map(); cache.set(load, byName); }
  const hit = byName.get(name);
  if (hit) return hit as unknown as ComponentType<P>;
  const C = lazy(async () => {
    const m = (await load()) as Record<string, unknown>;
    const exp = m[name] ?? (name === 'default' ? undefined : (m.default as Record<string, unknown> | undefined)?.[name]);
    if (typeof exp !== 'function' && typeof exp !== 'object') throw new Error(`Missing export ${name}`);
    return { default: exp as ComponentType<P> };
  });
  byName.set(name, C as unknown as ComponentType<never>);
  return C;
}

/** Loads a whole optional module (for helpers next to a component), or null. */
export async function loadOptional<T>(mods: Record<string, ModuleLoader>): Promise<T | null> {
  const load = firstLoader(mods);
  if (!load) return null;
  try { return (await load()) as T; } catch { return null; }
}

interface BoundaryProps { fallback: ReactNode; children: ReactNode }
export class SlotBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(_e: Error, _i: ErrorInfo) { /* the fallback stays; the feature's own tests cover it */ }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/** Renders `C` (from optionalComponent) with `props`, or `fallback` when absent, loading or failing. */
export function OptionalSlot<P extends object>({ C, props, fallback = null }: { C: ComponentType<P> | null; props: P; fallback?: ReactNode }) {
  if (!C) return <>{fallback}</>;
  return (
    <SlotBoundary fallback={fallback}>
      <Suspense fallback={fallback}><C {...props} /></Suspense>
    </SlotBoundary>
  );
}
